import {
  Button,
  ColumnChart,
  DateField,
  DonutChart,
  HookedForm,
  LineChart,
} from '@app/components';
import StarFullIcon from '@app/assets/icons/star-full.svg?react';
import { useAnalyticsRatings } from './AnalyticsRatings.hooks';

export const AnalyticsRatings = () => {
  const { 
    analyticsData, 
    loading, 
    error, 
    fetchAnalyticsData, 
    chartData, 
    platformChartData, 
    averageRatingChartData, 
  } = useAnalyticsRatings();

  const handleFormChange = (values: { start?: string; end?: string; connection?: string }) => {
    // Only call API if we have both start and end dates
    if (values.start && values.end) {
      fetchAnalyticsData({
        startDate: values.start,
        endDate: values.end,
        connection: values.connection,
      });
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-white p-8 text-center">
          <p>Loading analytics data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-white p-8 text-center">
          <p className="text-red-500">{error}</p>
          <Button onClick={() => fetchAnalyticsData()} className="mt-4">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Show message when no data is loaded yet
  if (!analyticsData) {
    return (
      <>
        <div className="bg-white border-b border-b-base-ghostWhite ">
          <div className="container mx-auto p-4 flex justify-between items-center">
            <h2 className="font-bold text-[28px]">Ratings analytics</h2>
          </div>
          <div className="container mx-auto p-4">
            <HookedForm className="flex gap-4" onChange={handleFormChange}>
              <DateField name="start" containerClass="w-36" />
              <DateField name="end" containerClass="w-36 " />
            </HookedForm>
          </div>
        </div>
        <div className="container mx-auto p-4">
          <div className="bg-white p-8 text-center">
            <p className="text-base-davyGray">Please select date filters to view analytics data</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="bg-white border-b border-b-base-ghostWhite ">
        <div className="container mx-auto p-4 flex justify-between items-center">
          <h2 className="font-bold text-[28px]">Ratings analytics</h2>
        </div>
        <div className="container mx-auto p-4">
          <HookedForm className="flex gap-4" onChange={handleFormChange}>
            <DateField name="start" containerClass="w-36" />
            <DateField name="end" containerClass="w-36 " />
          </HookedForm>
        </div>
      </div>
      <div className="container mx-auto p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white p-4 ">
            <div className="flex items-center justify-between">
              <h5 className="font-bold text-sm">Average rating</h5>
            </div>
            <hr className="my-4" />
            <div className="space-y-4 flex gap-10 items-center justify-center">
              <div>
                <h4 className="text-6xl font-bold">{analyticsData?.averageRating?.toFixed(1) || '0.0'}</h4>
                <p className="text-base-davyGray text-sm">{analyticsData?.totalReviews || 0} reviews</p>
              </div>
              <div>
                <ul>
                  {analyticsData?.ratingDistribution?.map((rating) => (
                    <li key={rating.rating} className="text-base-davyGray text-sm flex items-center gap-2">
                      <StarFullIcon />
                      <span>{rating.rating}</span>
                      <div className="rounded-full w-36 bg-brand-lightBlue3 h-2 overflow-hidden">
                        <div 
                          className="bg-brand-baseBlue h-full rounded-full" 
                          style={{ width: `${rating.percentage}%` }}
                        />
                      </div>
                      <span>{rating.count} ({rating.percentage.toFixed(1)}%)</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 h-fit">
            <div className="flex items-center justify-between">
              <h5 className="font-bold text-sm">Review count by platform</h5>
            </div>
            <hr className="my-4" />
            <div className="space-y-4 flex items-center gap-4">
              <DonutChart data={platformChartData} />
            </div>
          </div>
        </div>
        {(averageRatingChartData.length > 1 || chartData.length > 1) && (
          <div className="bg-white p-4 h-fit">
            <div className="flex items-center justify-between">
              <h5 className="font-bold text-sm">Rating distribution</h5>
            </div>
            <hr className="my-4" />
            <div>
              {averageRatingChartData.length > 1 && (
                <LineChart title="Rating" data={averageRatingChartData} />
              )}
              {chartData.length > 1 && (
                <ColumnChart data={chartData} />
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};
